import { Autocomplete, Box, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useCustomDashboardContext } from 'src/contexts/customDashboardContext/customDashboard.context';
import { useAuthentication } from 'src/features/authentication/context';
import { useGetAllTagsQuery } from 'src/hooks/reactQueryApiHooks';
import { NewSite } from 'src/types/sites/sites';

export const TagDrawerContent = () => {
  const {
    customerId: { value: customerId },
    sites,
  } = useAuthentication();

  const { selectedNode, updateNodeData, nodes } = useCustomDashboardContext();

  const [formValues, setFormValues] = useState({
    siteId: '',
    tagId: '',
    wellHeight: '',
  });

  const [selectedSiteId, setSelectedSiteId] = useState<string>('');

  const siteOptions: NewSite[] = useMemo(() => sites?.value ?? [], [sites]);

  const { tags } = useGetAllTagsQuery({ customerId: customerId!, siteId: selectedSiteId });

  const selectedTagObj = useMemo(() => {
    if (!tags) return null;
    return tags.find((a: any) => a.id === formValues.tagId) ?? null;
  }, [tags, formValues.tagId]);

  const onChangeHandler = (name: string, value: string | boolean | null) => {
    setFormValues((prevState) => {
      const updated = { ...prevState, [name]: value };

      if (selectedNode?.id) {
        updateNodeData(selectedNode.id, updated);
      }

      return updated;
    });
  };

  useEffect(() => {
    if (!selectedNode?.id) return;

    const findNode = nodes.find((node) => node.id === selectedNode?.id);

    const tagId = (findNode?.data as { tagId?: string })?.tagId ?? '';
    const siteId = (findNode?.data as { siteId?: string })?.siteId ?? '';
    const wellHeight = (findNode?.data as { wellHeight?: string })?.wellHeight ?? '';

    setFormValues({
      tagId,
      siteId,
      wellHeight,
    });
    setSelectedSiteId(siteId);
  }, [selectedNode?.id, nodes]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Autocomplete
        value={siteOptions.find((s) => s.id === formValues.siteId) ?? null}
        size="small"
        onChange={(_, selectedSite) => {
          if (selectedSite) {
            setSelectedSiteId(selectedSite.id);
            onChangeHandler('siteId', selectedSite.id);

            onChangeHandler('tagId', null);
            onChangeHandler('gatewayId', null);
          } else {
            setSelectedSiteId('');
            onChangeHandler('siteId', null);
            onChangeHandler('tagId', null);
            onChangeHandler('gatewayId', null);
          }
        }}
        renderInput={(params) => (
          <TextField {...params} label="Select Site" data-sm="select-site-input" />
        )}
        options={siteOptions}
        getOptionLabel={(option) => option.siteName}
        renderOption={(props, option) => (
          <li {...props} key={`${option.id}-${option.siteName}`}>
            {option.siteName}
          </li>
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
      <Autocomplete
        size="small"
        value={selectedTagObj}
        onChange={(_, selectedTag) => {
          if (selectedTag) {
            onChangeHandler('tagId', selectedTag.id);
            onChangeHandler('gatewayId', selectedTag.gatewayId);
          } else {
            onChangeHandler('tagId', null);
            onChangeHandler('gatewayId', null);
          }
        }}
        renderInput={(params) => (
          <TextField {...params} label="Select Tag" data-sm="select-tag-input" />
        )}
        options={tags ?? []}
        getOptionLabel={(option) => option?.displayName}
        renderOption={(props, option) => (
          <li {...props} key={`${option.id}-${option.displayName}`}>
            {option.displayName}
          </li>
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        disabled={!selectedSiteId}
      />
      <TextField
        size="small"
        value={formValues.wellHeight}
        label="Well Height"
        name="wellHeight"
        onChange={(e) => onChangeHandler('wellHeight', e.target.value)}
      />
    </Box>
  );
};
